import { hashString } from '@w/utils'
import type { NarrationSegment, SpeechPause } from '@w/content-blocks'
import type { SpeechHighlightTarget } from '@w/content-blocks-editor'

/** Chunking contract: `docs/use-cases/page-listening.spec.md` → Chunking. */
const MAX_CHUNK_CHARS = 600
const TOP_LEVEL = 3
/** Unforced cut points closer than this to the chunk start are skipped, per level. */
const MIN_CHUNK_CHARS = [0, 60, 120, 200]
const PAUSE_MS: Record<SpeechPause['reason'], number> = { section: 800, heading: 400 }

/** One sentence of one speech segment: the atom chunks are cut from. */
export type SpeechPiece = {
  blockId: string
  text: string
  lang: string | undefined
  /** Offset of the piece inside its block's spoken text. */
  offset: number
  label: boolean
  /** Rank of the cut point after this piece; `Infinity` where a cut is forced. */
  rank: number
  /** The pause the narration makes after this piece, if any. */
  pauseMs: number
  /** Last piece of its unit: joins the next piece with a paragraph break. */
  unitEnd: boolean
}

export type SpeechChunk = {
  text: string
  /** Sent to the voice; undefined lets it detect the language. */
  lang: string | undefined
  level: number
  /** Index of the chunk's first piece in the plan. */
  from: number
  pieces: (SpeechPiece & { start: number })[]
  /** Silence after the chunk, from the narration's own pauses. */
  pauseMs: number
}

export type SpeechPlan = {
  pieces: SpeechPiece[]
  /** Oversized sentences that were split, as `blockId: strategy`. */
  splits: string[]
  /** Chunks from `start` (a piece index at a unit start) to the end, on the ramp. */
  chunksFrom: (start: number) => SpeechChunk[]
  /** Piece indexes where units start, for navigation. */
  unitStarts: number[]
  /** The plan as readable lines, for debugging chunking decisions. */
  toString: () => string
}

export function planSpeech(segments: NarrationSegment[]): SpeechPlan {
  const pieces: SpeechPiece[] = []
  const splits: string[] = []
  segments.forEach((segment, index) => {
    if (segment.type === 'pause') {
      const last = pieces.at(-1)
      if (last) Object.assign(last, { rank: Infinity, pauseMs: PAUSE_MS[segment.reason], unitEnd: true })
      return
    }
    const sentences = sentencesOf(segment.text, segment.lang)
    if (sentences.strategy) splits.push(`${segment.blockId}: ${sentences.strategy}`)
    const next = segments.at(index + 1)
    const unitEnd = next?.type !== 'speech' || next.blockId !== segment.blockId
    let offset = segment.offset
    sentences.texts.forEach((text, position) => {
      const last = position === sentences.texts.length - 1
      const forced = last && (next?.type !== 'speech' || next.lang !== segment.lang)
      pieces.push({
        blockId: segment.blockId,
        text,
        lang: segment.lang,
        offset,
        label: segment.label === true,
        // A label is never cut from the text it introduces.
        rank: forced ? Infinity : segment.label ? 0 : Math.min(TOP_LEVEL, Math.max(trailingZeroBits(text), last && unitEnd ? 1 : 0)),
        pauseMs: 0,
        unitEnd: last && unitEnd,
      })
      offset += text.length + 1
    })
  })

  /** End (exclusive) of the level-`level` chunk starting at `start`. */
  const endOf = (start: number, level: number) => {
    let end = start
    let length = pieces[start].text.length
    while (end < pieces.length - 1 && (pieces[end].rank < level || (pieces[end].rank !== Infinity && (length < MIN_CHUNK_CHARS[level] || pieces[end].label)))) {
      end++
      length += joinLength(pieces[end - 1]) + pieces[end].text.length
    }
    end += 1
    // Too long: cut at the highest-ranked point that fits and keeps the minimum
    // size, the earliest on a tie.
    length = 0
    let best: number | undefined
    for (let index = start; index < end; index++) {
      length += (index > start ? joinLength(pieces[index - 1]) : 0) + pieces[index].text.length
      if (length > MAX_CHUNK_CHARS) return (best ?? Math.max(start, index - 1)) + 1
      const cuttable = !pieces[index].label || index === start
      if (cuttable && length >= MIN_CHUNK_CHARS[level] && (best === undefined || pieces[index].rank > pieces[best].rank)) best = index
    }
    return end
  }

  const chunksFrom = (start: number) => {
    const chunks: SpeechChunk[] = []
    let level = 0
    for (let from = start; from < pieces.length; level = Math.min(TOP_LEVEL, level + 1)) {
      const to = endOf(from, level)
      chunks.push(chunkOf(pieces.slice(from, to), level, from))
      from = to
    }
    return chunks
  }

  const unitStarts = pieces.flatMap((piece, index) => index === 0 || pieces[index - 1].unitEnd ? [index] : [])
  return {
    pieces,
    splits,
    chunksFrom,
    unitStarts,
    toString: () => [
      ...pieces.map((piece, index) => `${String(index).padStart(3)} ${piece.blockId} [${piece.lang ?? 'auto'}] rank=${piece.rank}${piece.pauseMs ? ` pause=${piece.pauseMs}` : ''} ${JSON.stringify(piece.text)}`),
      ...splits.map(split => `split ${split}`),
      ...chunksFrom(0).map((chunk, index) => `chunk ${index} L${chunk.level} [${chunk.lang ?? 'auto'}] ${chunk.text.length} chars ${JSON.stringify(chunk.text.slice(0, 60))}`),
    ].join('\n'),
  }
}

function chunkOf(slice: SpeechPiece[], level: number, from: number): SpeechChunk {
  let text = ''
  const pieces = slice.map((piece, index) => {
    if (index > 0) text += joinAfter(slice[index - 1])
    const start = text.length
    text += piece.text
    return { ...piece, start }
  })
  return { text, lang: slice[0].lang, level, from, pieces, pauseMs: slice.at(-1)?.pauseMs ?? 0 }
}

/** What joins a piece to the next one in a chunk: a paragraph break after a unit. */
function joinAfter(piece: SpeechPiece) {
  return piece.unitEnd ? '\n\n' : ' '
}

function joinLength(piece: SpeechPiece) {
  return joinAfter(piece).length
}

function trailingZeroBits(text: string) {
  const nibble = Number.parseInt(hashString(text).slice(-1), 16)
  return nibble === 0 ? 4 : Math.log2(nibble & -nibble)
}

/** Sentences by `Intl.Segmenter`; a sentence over the limit splits at clause
 * breaks, then at words, as the audiobook segmenter does. */
function sentencesOf(text: string, lang: string | undefined): { texts: string[]; strategy?: 'clause' | 'word' } {
  const sentences = Array.from(new Intl.Segmenter(lang, { granularity: 'sentence' }).segment(text), part => part.segment.trim()).filter(Boolean)
  let strategy: 'clause' | 'word' | undefined
  const texts = sentences.flatMap(sentence => {
    if (sentence.length <= MAX_CHUNK_CHARS) return [sentence]
    const clauses = pack(sentence.split(/(?<=[,;:—])\s+/u))
    if (clauses.every(clause => clause.length <= MAX_CHUNK_CHARS)) {
      strategy ??= 'clause'
      return clauses
    }
    strategy = 'word'
    return pack(sentence.split(/\s+/u))
  })
  return { texts, ...(strategy && { strategy }) }
}

function pack(parts: string[]) {
  const packed: string[] = []
  for (const part of parts) {
    const last = packed.at(-1)
    if (last !== undefined && last.length + 1 + part.length <= MAX_CHUNK_CHARS) packed[packed.length - 1] = `${last} ${part}`
    else packed.push(part)
  }
  return packed
}

export type SpeechWordTiming = { word: string; startMs: number; endMs: number }

export type SpokenWord = {
  /** Index of the word's piece in the plan. */
  piece: number
  blockId: string
  text: string
  /** Offset inside the block's spoken text. */
  offset: number
  startMs: number
  endMs: number
  label: boolean
}

function core(word: string) {
  return word.replaceAll(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLocaleLowerCase()
}

/** Aligns provider word timings to the chunk text in order. A word the provider
 * spelled differently is skipped instead of shifting every later highlight. */
export function spokenWords(chunk: SpeechChunk, timings: SpeechWordTiming[]): SpokenWord[] {
  const text = chunk.text.toLocaleLowerCase()
  let cursor = 0
  const words: SpokenWord[] = []
  for (const timing of timings) {
    const needle = core(timing.word)
    if (!needle) continue
    const found = text.indexOf(needle, cursor)
    if (found === -1) continue
    cursor = found + needle.length
    const index = Math.max(0, chunk.pieces.findLastIndex(candidate => candidate.start <= found))
    const piece = chunk.pieces[index]
    words.push({ piece: chunk.from + index, blockId: piece.blockId, text: chunk.text.slice(found, cursor), offset: piece.offset + found - piece.start, startMs: timing.startMs, endMs: timing.endMs, label: piece.label })
  }
  return words
}

/** The word being spoken `ms` into a chunk. */
export function wordAt(words: SpokenWord[], ms: number): SpokenWord | undefined {
  return words.findLast(candidate => candidate.startMs <= ms) ?? words.at(0)
}

/** What to paint for a spoken word: the word, or only its block while a label
 * (not on the page) is spoken. */
export function speechTarget(word: SpokenWord): SpeechHighlightTarget {
  return word.label ? { blockId: word.blockId } : { blockId: word.blockId, word: { text: word.text, offset: word.offset } }
}
